import type { VerticalNavItems } from '@layouts/types'

/**
 * Admin Navigation Items
 *
 * Each item can have `action` and `subject` properties for ACL filtering:
 * - action: The CASL action (e.g., 'read', 'create', 'manage', 'access')
 * - subject: The CASL subject in PascalCase (e.g., 'Camps', 'Users', 'AdminPanel')
 *
 * Navigation items without action/subject are shown to all authenticated users.
 * Groups/children will be hidden if no children are visible.
 *
 * Permission resources (snake_case) map to subjects (PascalCase):
 * - admin_panel → AdminPanel
 * - camps → Camps
 * - bookings → Bookings
 * - participants → Participants
 * - attendance → Attendance
 * - faqs → Faqs
 * - monitors → Monitors
 * - finance → Finance
 * - users → Users
 * - roles → Roles
 * - forms → Forms
 * - contact_messages → ContactMessages
 * - tickets → Tickets
 * - email_queue → EmailQueue
 * - logs → Logs
 * - settings → Settings
 * - maintenance → Maintenance
 * - reports → Reports
 * - system → System
 * - certificates → Certificates
 * - feest_feeds → FeestFeeds
 */
export default [
  // Website link - visible to all admin users
  {
    title: 'Website',
    icon: { icon: 'bx-world' },
    to: 'index',
    action: 'access',
    subject: 'AdminPanel',
    target: '_blank' as const,
  },

  // Dashboard - requires admin panel access
  {
    title: 'Dashboard',
    icon: { icon: 'bx-bxs-dashboard' },
    to: 'admin-kampen-dashboard',
    action: 'access',
    subject: 'AdminPanel',
  },

  // ============================================================
  // KAMPEN SECTION
  // ============================================================
  {
    heading: 'Kampen',
    action: 'read',
    subject: 'Camps',
  },
  {
    title: 'Kampen overzicht',
    icon: { icon: 'bx-bxs-compass' },
    to: 'admin-kampen',
    action: 'read',
    subject: 'Camps',
  },
  {
    title: 'Deelnemers',
    icon: { icon: 'bx-calendar-check' },
    to: 'admin-bookings-deelnemers',
    action: 'read',
    subject: 'Participants',
  },
  {
    title: 'Aanwezigheid',
    icon: { icon: 'bx-user-check' },
    to: 'admin-aanwezigheid',
    action: 'read',
    subject: 'Attendance',
  },
  {
    title: 'Bestellingen',
    icon: { icon: 'bx-money' },
    to: 'admin-bookings-bestellingen',
    action: 'read',
    subject: 'Bookings',
  },
  {
    title: 'Fiscale Attesten',
    icon: { icon: 'bx-file' },
    to: 'admin-certificates-fiscale-attesten',
    action: 'read',
    subject: 'Certificates',
  },
  {
    title: 'Mutualiteit',
    icon: { icon: 'bx-first-aid' },
    to: 'admin-certificates-mutualiteit',
    action: 'read',
    subject: 'Certificates',
  },
  {
    title: 'Wachtlijst',
    icon: { icon: 'bx-list-ul' },
    to: 'admin-bookings-wachtlijst',
    action: 'read',
    subject: 'Waitlist',
  },
  {
    title: 'Preregistratie',
    icon: { icon: 'bx-bell' },
    to: 'admin-bookings-preregistratie',
    action: 'read',
    subject: 'PreRegistrations',
  },
  {
    title: 'Uitnodigingen',
    icon: { icon: 'bx-link' },
    to: 'admin-bookings-uitnodigingen',
    action: 'manage',
    subject: 'Bookings',
  },

  // ============================================================
  // WEBSITE CONTENT SECTION
  // ============================================================
  {
    heading: 'Website',
    action: 'access',
    subject: 'AdminPanel',
  },
  {
    title: 'Blog',
    icon: { icon: 'bx-news' },
    to: 'admin-content-blog',
    action: 'read',
    subject: 'Blog',
  },
  {
    title: 'FAQ',
    icon: { icon: 'bx-help-circle' },
    to: 'admin-content-faq',
    action: 'read',
    subject: 'Faqs',
  },
  {
    title: 'Feest Feeds',
    icon: { icon: 'bx-party' },
    to: 'admin-content-feest-feeds',
    action: 'read',
    subject: 'FeestFeeds',
  },
  {
    title: 'Partners',
    icon: { icon: 'bx-building' },
    to: 'admin-content-partners',
    action: 'manage',
    subject: 'Camps',
  },
  {
    title: 'Monitoren',
    icon: { icon: 'bx-user' },
    to: 'admin-users-monitoren',
    action: 'read',
    subject: 'Monitors',
  },

  // ============================================================
  // ADMIN SECTION
  // ============================================================
  {
    heading: 'Admin',
    action: 'read',
    subject: 'Users',
  },
  {
    title: 'Gebruikers',
    icon: { icon: 'bx-user' },
    action: 'manage',
    subject: 'Users',
    children: [
      {
        title: 'Lijst',
        icon: { icon: 'bx-bxs-bullseye' },
        to: 'admin-users-list',
        action: 'manage',
        subject: 'Users',
      },
      {
        title: 'Toegang',
        icon: { icon: 'bx-bxs-check-shield' },
        to: 'admin-users-roles-permissions',
        action: 'manage',
        subject: 'Roles',
      },
    ],
  },
  {
    title: 'Kampen',
    icon: { icon: 'bx-bxs-compass' },
    action: 'manage',
    subject: 'Camps',
    children: [
      {
        title: 'Formulieren',
        icon: { icon: 'bx-file' },
        to: 'admin-content-forms',
        action: 'read',
        subject: 'Forms',
      },
      {
        title: 'Tickets',
        icon: { icon: 'bx-wallet' },
        to: 'admin-content-tickets',
        action: 'read',
        subject: 'Tickets',
      },
      {
        title: 'Tags & Categorieën',
        icon: { icon: 'bx-tag' },
        to: 'admin-content-tags-categorien',
        action: 'read',
        subject: 'ParticipantTags',
      },
      {
        title: 'Kortingen & Coupons',
        icon: { icon: 'bx-purchase-tag-alt' },
        to: 'admin-content-kortingen-coupons',
        action: 'read',
        subject: 'Finance',
      },
    ],
  },
  {
    title: 'Emails',
    icon: { icon: 'bx-envelope' },
    action: 'manage',
    subject: 'EmailQueue',
    children: [
      {
        title: 'Email Beheer',
        icon: { icon: 'bx-envelope' },
        to: 'admin-email-management',
        action: 'read',
        subject: 'EmailQueue',
      },
      {
        title: 'E-mail Testcenter',
        icon: { icon: 'bx-send' },
        to: 'admin-email-test',
        action: 'manage',
        subject: 'EmailQueue',
      },
      {
        title: 'Contactberichten',
        icon: { icon: 'bx-message-square-dots' },
        to: 'admin-contact-messages',
        action: 'read',
        subject: 'ContactMessages',
      },
    ],
  },

  // ============================================================
  // SYSTEEM SECTION
  // ============================================================
  {
    heading: 'Systeem',
    action: 'read',
    subject: 'System',
  },
  {
    title: 'Analytics',
    icon: { icon: 'bx-bar-chart-alt-2' },
    to: 'admin-analytics',
    action: 'read',
    subject: 'Reports',
  },
  {
    title: 'Systeem Logs',
    icon: { icon: 'bx-error' },
    to: 'admin-logs',
    action: 'read',
    subject: 'Logs',
  },
  {
    title: 'Instellingen',
    icon: { icon: 'bx-cog' },
    action: 'read',
    subject: 'Settings',
    children: [
      {
        title: 'Onderhoud & Toegang',
        to: 'admin-system-settings-maintenance',
        action: 'read',
        subject: 'Maintenance',
      },
      {
        title: 'E-mail',
        to: 'admin-system-settings-email',
        action: 'read',
        subject: 'Settings',
      },
    ],
  },
] as VerticalNavItems
